import { initializeApp } from 'firebase/app';
import { initializeAuth, indexedDBLocalPersistence, browserPopupRedirectResolver, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentSingleTabManager } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { Capacitor } from '@capacitor/core';
// @ts-ignore
import firebaseConfig from '../firebase-applet-config.json';

// Exported so src/lib/analytics.ts can attach Firebase Analytics to the same
// app instance. Everything else should keep importing `db` / `auth` directly.
export const app = initializeApp(firebaseConfig);
// Firestore transport, branched by platform:
//   • WEB → getFirestore (default WebChannel streaming — works fine in real browsers).
//   • NATIVE → initializeFirestore with forced long-polling. In the Capacitor
//     WKWebView, Firestore's streaming channels constantly drop ("network connection
//     was lost (channel)") and the churn destabilizes the shared HTTP/2 connection —
//     which kills unrelated long-running requests mid-flight (the GameChanger import's
//     ~15-30s Claude call was dying at ~5s). Long-polling never opens those streaming
//     channels, so the connection stays stable. Native-only; web is unchanged.
//     NOTE: initializeFirestore MUST run before any other Firestore use of `db`.
// Native also keeps a PERSISTENT (IndexedDB) cache so the app works at fields with
// no signal: the team, roster and games load from the phone, and writes made
// offline survive an app restart and upload when the connection returns. The
// default memory cache lost both on every cold start, which opened the app into
// the create-team wizard (empty team query) and dropped unsynced pitches.
// Single-tab with forceOwnership: the WKWebView is the only client, and forcing
// ownership stops a lingering lease (e.g. after a web view reload) from pushing
// Firestore back to memory cache. If IndexedDB can't open, Firestore falls back
// to the memory cache on its own. Web stays memory-only: a persisted cache on a
// shared computer would outlive sign-out.
export const db = Capacitor.isNativePlatform()
  ? initializeFirestore(app, {
      experimentalForceLongPolling: true,
      localCache: persistentLocalCache({ tabManager: persistentSingleTabManager({ forceOwnership: true }) }),
    })
  : getFirestore(app);
// On WEB, signInWithPopup needs a popupRedirectResolver (initializeAuth omits the
// default that getAuth provides) — without it web Google sign-in throws
// auth/argument-error. On NATIVE we must NOT set it: native signs in via the
// GoogleSignInPlugin credential flow, and including the web resolver makes Firebase
// try to process a redirect result on startup, which fails in the Capacitor
// WKWebView's storage-partitioned environment ("missing initial state").
export const auth = initializeAuth(app, {
  persistence: indexedDBLocalPersistence,
  ...(Capacitor.isNativePlatform() ? {} : { popupRedirectResolver: browserPopupRedirectResolver }),
});
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider(); googleProvider.setCustomParameters({ prompt: "select_account" });


export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}
